
import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription,
  CardFooter
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useToast } from "@/hooks/use-toast";
import { 
  Upload, 
  Calendar, 
  AlignLeft, 
  Video, 
  Mail, 
  Instagram, 
  TwitchIcon, 
  Send,
  CheckCircle,
  Clock,
  AlertTriangle,
  ExternalLink,
  Play,
  PauseCircle,
  Edit,
  Image as ImageIcon,
  FileText,
  FileSpreadsheet
} from 'lucide-react';

interface CampaignStrategy {
  id: string;
  phase: 'pre-launch' | 'launch' | 'post-launch';
  title: string;
  description: string;
  status: 'pending' | 'scheduled' | 'in-progress' | 'completed';
  type: 'social' | 'email' | 'video' | 'press';
  platform?: string;
  date: string;
}

interface CampaignAsset {
  id: string;
  title: string;
  type: 'image' | 'video' | 'caption' | 'email';
  thumbnail?: string;
  content?: string;
  platform?: string;
}

// Mock data for campaign strategies
const MOCK_STRATEGIES: CampaignStrategy[] = [
  {
    id: '1',
    phase: 'pre-launch',
    title: 'Teaser Video Post',
    description: '15-second teaser clip with song snippet',
    status: 'scheduled',
    type: 'social',
    platform: 'instagram',
    date: '2023-08-10'
  },
  {
    id: '2',
    phase: 'pre-launch',
    title: 'Behind the Scenes Photos',
    description: 'Studio session photos with caption about the creative process',
    status: 'scheduled',
    type: 'social',
    platform: 'instagram',
    date: '2023-08-12'
  },
  {
    id: '3',
    phase: 'pre-launch',
    title: 'Fan Newsletter',
    description: 'Email to subscribers announcing upcoming release',
    status: 'pending',
    type: 'email',
    date: '2023-08-14'
  },
  {
    id: '4',
    phase: 'launch',
    title: 'Release Announcement',
    description: 'Official release post with streaming links',
    status: 'pending',
    type: 'social',
    platform: 'all',
    date: '2023-08-18'
  },
  {
    id: '5',
    phase: 'launch',
    title: 'TikTok Challenge',
    description: 'Launch dance challenge using song hook',
    status: 'pending',
    type: 'social',
    platform: 'tiktok',
    date: '2023-08-18'
  },
  {
    id: '6',
    phase: 'post-launch',
    title: 'Lyrics Video',
    description: 'Full lyrics video for YouTube and IGTV',
    status: 'pending',
    type: 'video',
    platform: 'youtube',
    date: '2023-08-25'
  },
  {
    id: '7',
    phase: 'post-launch',
    title: 'Fan Reactions Compilation',
    description: 'Compilation of fan reactions and reposts',
    status: 'pending',
    type: 'social',
    platform: 'instagram',
    date: '2023-08-30'
  }
];

// Mock data for campaign assets
const MOCK_ASSETS: CampaignAsset[] = [
  {
    id: '1',
    title: 'Cover Art Reveal',
    type: 'image',
    thumbnail: 'https://placehold.co/300x300/292929/e6e6e6?text=Cover+Art',
    platform: 'instagram'
  },
  {
    id: '2',
    title: 'Teaser Video',
    type: 'video',
    thumbnail: 'https://placehold.co/300x200/292929/e6e6e6?text=Teaser+Video',
    platform: 'all'
  },
  {
    id: '3',
    title: 'Release Announcement',
    type: 'caption',
    content: "I'm beyond excited to announce my new single '[SONG NAME]' dropping on [DATE]. This track means so much to me and I can't wait for you all to hear it! Pre-save link in bio. 🎵✨ #newmusic #newsingle",
    platform: 'instagram'
  },
  {
    id: '4',
    title: 'Fan Newsletter',
    type: 'email',
    content: "Subject: [SONG TITLE] Drops This Friday!\n\nHey [FIRST_NAME],\n\nI'm thrilled to announce my new single '[SONG TITLE]' will be available on all platforms this Friday! This track is special to me because [insert personal story].\n\nBe among the first to listen by pre-saving here: [LINK]\n\nCan't wait for you to hear it!\n\n[YOUR NAME]",
  }
];

const getPlatformIcon = (platform: string) => {
  switch (platform) {
    case 'instagram':
      return <Instagram className="h-4 w-4" />;
    case 'tiktok':
      return <TwitchIcon className="h-4 w-4" />; // Using Twitch as a stand-in for TikTok
    case 'youtube':
      return <Play className="h-4 w-4" />;
    case 'all':
      return <ExternalLink className="h-4 w-4" />;
    default:
      return null;
  }
};

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'social':
      return <Instagram className="h-4 w-4" />;
    case 'email':
      return <Mail className="h-4 w-4" />;
    case 'video':
      return <Video className="h-4 w-4" />;
    case 'press':
      return <FileText className="h-4 w-4" />;
    default:
      return null;
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'scheduled':
      return <Calendar className="h-4 w-4 text-blue-500" />;
    case 'in-progress':
      return <Clock className="h-4 w-4 text-amber-500" />;
    case 'pending':
      return <AlertTriangle className="h-4 w-4 text-gray-400" />;
    default:
      return null;
  }
};

const getAssetTypeIcon = (type: string) => {
  switch (type) {
    case 'image':
      return <ImageIcon className="h-4 w-4" />;
    case 'video':
      return <Video className="h-4 w-4" />;
    case 'caption':
      return <AlignLeft className="h-4 w-4" />;
    case 'email':
      return <Mail className="h-4 w-4" />;
    default:
      return null;
  }
};

const AutoPromoCampaigns: React.FC = () => {
  const { toast } = useToast();
  const [isCreating, setIsCreating] = useState(false);
  const [strategies, setStrategies] = useState<CampaignStrategy[]>(MOCK_STRATEGIES);
  const [assets, setAssets] = useState<CampaignAsset[]>(MOCK_ASSETS);
  const [isUploading, setIsUploading] = useState(false);
  const [activeTab, setActiveTab] = useState("campaign-strategy");

  const handleGenerateCampaign = () => {
    setIsUploading(true);
    
    // Simulate upload and processing
    setTimeout(() => {
      setIsUploading(false);
      setIsCreating(true);
      
      // Simulate AI generation
      setTimeout(() => {
        setIsCreating(false);
        
        toast({
          title: "Campaign generated!",
          description: "Your AI-generated marketing campaign is ready to launch"
        });
      }, 2500);
    }, 1500);
  };
  
  const handleLaunch = () => {
    toast({
      title: "Campaign launched!",
      description: "Your marketing campaign is now active. All scheduled content will be posted automatically."
    });
  };
  
  const handleSchedule = (strategyId: string) => {
    setStrategies(strategies.map(s => 
      s.id === strategyId ? {...s, status: 'scheduled'} : s
    ));
    
    toast({
      title: "Content scheduled",
      description: "This item has been added to your scheduled content."
    });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-bold mb-1">Auto-Promo Campaigns</h2>
          <p className="text-muted-foreground">Generate complete marketing campaigns for your new music</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-background/70">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <div>
                  <CardTitle>New Release Campaign</CardTitle>
                  <CardDescription>Full marketing strategy for your upcoming release</CardDescription>
                </div>
                
                {!isCreating && !isUploading && (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => toast({ title: "Template saved", description: "This campaign has been saved as a template" })}
                    >
                      Save Template
                    </Button>
                    <Button 
                      variant="default" 
                      onClick={handleLaunch}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Launch Campaign
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            
            <CardContent>
              {(isUploading || isCreating) ? (
                <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                  {isUploading ? (
                    <>
                      <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                      <h3 className="text-xl font-semibold mb-2">Uploading Your Track</h3>
                      <p className="text-muted-foreground max-w-lg">
                        We're analyzing your music to create a personalized marketing campaign 
                        that highlights its unique qualities...
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                      <h3 className="text-xl font-semibold mb-2">Generating Campaign</h3>
                      <p className="text-muted-foreground max-w-lg">
                        Our AI is creating your customized campaign including social media posts, 
                        email templates, and content ideas to promote your release...
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <div>
                  <Tabs 
                    defaultValue="campaign-strategy" 
                    value={activeTab}
                    onValueChange={setActiveTab}
                    className="w-full"
                  >
                    <TabsList className="grid grid-cols-2 w-full">
                      <TabsTrigger value="campaign-strategy">
                        <Calendar className="h-4 w-4 mr-2" />
                        Campaign Strategy
                      </TabsTrigger>
                      <TabsTrigger value="content-assets">
                        <ImageIcon className="h-4 w-4 mr-2" />
                        Content Assets
                      </TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="campaign-strategy" className="space-y-4 pt-4">
                      <Accordion type="multiple" defaultValue={['pre-launch', 'launch', 'post-launch']} className="space-y-4">
                        <AccordionItem value="pre-launch" className="border rounded-md">
                          <AccordionTrigger className="px-4">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="bg-blue-500/10 text-blue-500 border-blue-500/30">
                                Pre-Launch
                              </Badge>
                              <span className="text-sm text-muted-foreground">
                                Build anticipation before release
                              </span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 space-y-3">
                            {strategies
                              .filter(s => s.phase === 'pre-launch')
                              .map(strategy => (
                                <div key={strategy.id} className="border rounded-md p-3 bg-muted/20">
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                    <div className="flex items-start gap-3">
                                      <div className="bg-muted/30 p-2 rounded-md">
                                        {getTypeIcon(strategy.type)}
                                      </div>
                                      <div>
                                        <h4 className="font-medium flex items-center gap-2">
                                          {strategy.title}
                                          {strategy.platform && getPlatformIcon(strategy.platform) && (
                                            <Badge variant="outline" className="text-xs flex items-center gap-1">
                                              {getPlatformIcon(strategy.platform)}
                                              <span className="capitalize">{strategy.platform}</span>
                                            </Badge>
                                          )}
                                        </h4>
                                        <p className="text-sm text-muted-foreground">{strategy.description}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2 self-end sm:self-auto">
                                      <Badge 
                                        variant="outline" 
                                        className="flex items-center gap-1"
                                      >
                                        {getStatusIcon(strategy.status)}
                                        <span className="capitalize">{strategy.status}</span>
                                      </Badge>
                                      
                                      {strategy.status === 'pending' ? (
                                        <Button 
                                          size="sm" 
                                          variant="outline"
                                          onClick={() => handleSchedule(strategy.id)}
                                        >
                                          Schedule
                                        </Button>
                                      ) : (
                                        <Button 
                                          size="sm" 
                                          variant="outline"
                                        >
                                          <Edit className="h-3.5 w-3.5" />
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                            ))}
                          </AccordionContent>
                        </AccordionItem>
                        
                        <AccordionItem value="launch" className="border rounded-md">
                          <AccordionTrigger className="px-4">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/30">
                                Launch
                              </Badge>
                              <span className="text-sm text-muted-foreground">
                                Maximum visibility on release day
                              </span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 space-y-3">
                            {strategies
                              .filter(s => s.phase === 'launch')
                              .map(strategy => (
                                <div key={strategy.id} className="border rounded-md p-3 bg-muted/20">
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                    <div className="flex items-start gap-3">
                                      <div className="bg-muted/30 p-2 rounded-md">
                                        {getTypeIcon(strategy.type)}
                                      </div>
                                      <div>
                                        <h4 className="font-medium flex items-center gap-2">
                                          {strategy.title}
                                          {strategy.platform && getPlatformIcon(strategy.platform) && (
                                            <Badge variant="outline" className="text-xs flex items-center gap-1">
                                              {getPlatformIcon(strategy.platform)}
                                              <span className="capitalize">{strategy.platform}</span>
                                            </Badge>
                                          )}
                                        </h4>
                                        <p className="text-sm text-muted-foreground">{strategy.description}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2 self-end sm:self-auto">
                                      <Badge 
                                        variant="outline" 
                                        className="flex items-center gap-1"
                                      >
                                        {getStatusIcon(strategy.status)}
                                        <span className="capitalize">{strategy.status}</span>
                                      </Badge>
                                      
                                      {strategy.status === 'pending' ? (
                                        <Button 
                                          size="sm" 
                                          variant="outline"
                                          onClick={() => handleSchedule(strategy.id)}
                                        >
                                          Schedule
                                        </Button>
                                      ) : (
                                        <Button 
                                          size="sm" 
                                          variant="outline"
                                        >
                                          <Edit className="h-3.5 w-3.5" />
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                            ))}
                          </AccordionContent>
                        </AccordionItem>
                        
                        <AccordionItem value="post-launch" className="border rounded-md">
                          <AccordionTrigger className="px-4">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="bg-purple-500/10 text-purple-500 border-purple-500/30">
                                Post-Launch
                              </Badge>
                              <span className="text-sm text-muted-foreground">
                                Maintain momentum after release
                              </span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 space-y-3">
                            {strategies
                              .filter(s => s.phase === 'post-launch')
                              .map(strategy => (
                                <div key={strategy.id} className="border rounded-md p-3 bg-muted/20">
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                    <div className="flex items-start gap-3">
                                      <div className="bg-muted/30 p-2 rounded-md">
                                        {getTypeIcon(strategy.type)}
                                      </div>
                                      <div>
                                        <h4 className="font-medium flex items-center gap-2">
                                          {strategy.title}
                                          {strategy.platform && getPlatformIcon(strategy.platform) && (
                                            <Badge variant="outline" className="text-xs flex items-center gap-1">
                                              {getPlatformIcon(strategy.platform)}
                                              <span className="capitalize">{strategy.platform}</span>
                                            </Badge>
                                          )}
                                        </h4>
                                        <p className="text-sm text-muted-foreground">{strategy.description}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2 self-end sm:self-auto">
                                      <Badge 
                                        variant="outline" 
                                        className="flex items-center gap-1"
                                      >
                                        {getStatusIcon(strategy.status)}
                                        <span className="capitalize">{strategy.status}</span>
                                      </Badge>
                                      
                                      {strategy.status === 'pending' ? (
                                        <Button 
                                          size="sm" 
                                          variant="outline"
                                          onClick={() => handleSchedule(strategy.id)}
                                        >
                                          Schedule
                                        </Button>
                                      ) : (
                                        <Button 
                                          size="sm" 
                                          variant="outline"
                                        >
                                          <Edit className="h-3.5 w-3.5" />
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                            ))}
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    </TabsContent>
                    
                    <TabsContent value="content-assets" className="space-y-4 pt-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {assets.map(asset => (
                          <Card key={asset.id} className="overflow-hidden">
                            <div className="flex flex-col">
                              <div className="flex items-center justify-between p-3 bg-muted/20">
                                <div className="flex items-center gap-2">
                                  {getAssetTypeIcon(asset.type)}
                                  <h3 className="font-medium">{asset.title}</h3>
                                </div>
                                {asset.platform && (
                                  <Badge variant="outline" className="text-xs flex items-center gap-1">
                                    {getPlatformIcon(asset.platform)}
                                    <span className="capitalize">{asset.platform}</span>
                                  </Badge>
                                )}
                              </div>
                              
                              {asset.thumbnail && (
                                <div className="aspect-video bg-muted/30 relative group overflow-hidden">
                                  <img 
                                    src={asset.thumbnail} 
                                    alt={asset.title} 
                                    className="w-full h-full object-cover"
                                  />
                                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <Button variant="secondary" size="sm" className="flex items-center gap-1">
                                      {asset.type === 'video' ? (
                                        <>
                                          <Play className="h-3.5 w-3.5" />
                                          Preview
                                        </>
                                      ) : (
                                        <>
                                          <Edit className="h-3.5 w-3.5" />
                                          Edit
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              )}
                              
                              {asset.content && (
                                <div className="p-3 max-h-48 overflow-y-auto bg-muted/10">
                                  <p className="text-sm whitespace-pre-line">{asset.content}</p>
                                </div>
                              )}
                              
                              <div className="p-3 flex justify-end gap-2 bg-muted/20">
                                <Button variant="outline" size="sm">
                                  Edit
                                </Button>
                                <Button variant="default" size="sm">
                                  Use
                                </Button>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        <div className="space-y-6">
          {!isCreating && !isUploading && (
            <Card>
              <CardHeader>
                <CardTitle>Create New Campaign</CardTitle>
                <CardDescription>Upload your music to generate a marketing campaign</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="border-2 border-dashed rounded-md border-muted-foreground/20 p-6 text-center hover:border-muted-foreground/40 transition-colors cursor-pointer">
                  <Upload className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mb-1">
                    Drag and drop or click to upload your track
                  </p>
                  <p className="text-xs text-muted-foreground">
                    MP3, WAV up to 20MB
                  </p>
                </div>
                
                <Button
                  onClick={handleGenerateCampaign}
                  variant="default"
                  className="w-full"
                >
                  Generate Campaign
                </Button>
              </CardContent>
            </Card>
          )}
          
          <Card>
            <CardHeader>
              <CardTitle>Campaign Analytics</CardTitle>
              <CardDescription>Track the success of your campaigns</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-medium mb-2">Summer EP Campaign</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Reach</span>
                      <span className="font-medium">12,486 people</span>
                    </div>
                    <div className="h-2 bg-muted/30 rounded-full overflow-hidden">
                      <div className="h-full bg-green-500 rounded-full" style={{ width: '75%' }}></div>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mt-3">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Engagement</span>
                      <span className="font-medium">2,841 interactions</span>
                    </div>
                    <div className="h-2 bg-muted/30 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full" style={{ width: '65%' }}></div>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mt-3">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Conversion</span>
                      <span className="font-medium">842 streams</span>
                    </div>
                    <div className="h-2 bg-muted/30 rounded-full overflow-hidden">
                      <div className="h-full bg-purple-500 rounded-full" style={{ width: '35%' }}></div>
                    </div>
                  </div>
                </div>
                
                <Separator />
                
                <div>
                  <h3 className="text-sm font-medium mb-2">Previous Campaigns</h3>
                  <div className="space-y-3">
                    <div className="p-2 bg-muted/30 rounded-md">
                      <div className="flex justify-between items-center mb-1">
                        <h4 className="font-medium">Spring Single</h4>
                        <Badge variant="outline" className="text-xs">Completed</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">11K reach, 4.2K engagement</p>
                    </div>
                    
                    <div className="p-2 bg-muted/30 rounded-md">
                      <div className="flex justify-between items-center mb-1">
                        <h4 className="font-medium">Christmas Release</h4>
                        <Badge variant="outline" className="text-xs">Completed</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">21K reach, 6.8K engagement</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="border-t bg-muted/20 justify-center py-2">
              <Button variant="link" className="text-sm">View detailed analytics</Button>
            </CardFooter>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Campaign Templates</CardTitle>
              <CardDescription>Ready-to-use marketing templates</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[220px]">
                <div className="space-y-3 pr-4">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start gap-2 text-left"
                    onClick={() => {
                      toast({
                        title: "Template selected",
                        description: "Single Release template has been loaded"
                      });
                    }}
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                    <div className="flex flex-col items-start">
                      <span>Single Release</span>
                      <span className="text-xs text-muted-foreground">30-day campaign</span>
                    </div>
                  </Button>
                  
                  <Button variant="outline" className="w-full justify-start gap-2 text-left">
                    <FileSpreadsheet className="h-4 w-4" />
                    <div className="flex flex-col items-start">
                      <span>Album Launch</span>
                      <span className="text-xs text-muted-foreground">60-day campaign</span>
                    </div>
                  </Button>
                  
                  <Button variant="outline" className="w-full justify-start gap-2 text-left">
                    <FileSpreadsheet className="h-4 w-4" />
                    <div className="flex flex-col items-start">
                      <span>Music Video</span>
                      <span className="text-xs text-muted-foreground">2-week campaign</span>
                    </div>
                  </Button>
                  
                  <Button variant="outline" className="w-full justify-start gap-2 text-left">
                    <FileSpreadsheet className="h-4 w-4" />
                    <div className="flex flex-col items-start">
                      <span>Tour Promotion</span>
                      <span className="text-xs text-muted-foreground">90-day campaign</span>
                    </div>
                  </Button>
                  
                  <Button variant="outline" className="w-full justify-start gap-2 text-left">
                    <FileSpreadsheet className="h-4 w-4" />
                    <div className="flex flex-col items-start">
                      <span>Indie Release</span>
                      <span className="text-xs text-muted-foreground">Low-budget campaign</span>
                    </div>
                  </Button>
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AutoPromoCampaigns;
